/**
 * Provides the `processCSV` utility function for processing CSV data, and its associated `ProcessCsvOptions` and `ProcessCsvData` types.
 * @module processCSV
 */
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
/**
 * Processes raw CSV text to extract column headers and count the number of data rows.
 *
 * **Default behavior:**
 * By default (when no `options` are provided or `options.hasHeaders` is true):
 * - Assumes the first row (index 0) contains headers.
 * - Uses a comma (`,`) as the column separator.
 * - Splits the input string by newline characters (`\n`) to identify rows.
 * - Filters out any rows that are completely empty after trimming whitespace.
 * - Extracts headers from the specified `headerRow` (defaulting to 0), trimming whitespace from each header.
 * - Counts the remaining rows as data rows (`numberOfRows`).
 * - Returns an object `{ type: 'csv', headers: string[], numberOfRows: number }`.
 *
 * If `options.hasHeaders` is set to `false`:
 * - Generates generic headers (e.g., "Column1", "Column2", ...) based on the number of columns in the first data row.
 * - Counts all non-empty rows as data rows.
 *
 * Handles null, undefined, or empty `csvText` input by returning `{ type: 'csv', headers: [], numberOfRows: 0 }`.
 *
 * @since 5.10 Initial implementation.
 * @param {string | null | undefined} csvText - The raw CSV data as a single string, with rows separated by newline characters (`\n`). Can be null or undefined.
 * @param {ProcessCsvOptions} [options={}] - Optional configuration for processing the CSV.
 * @param {number} [options.headerRow=0] - The 0-based index of the row containing headers. Only used if `hasHeaders` is true. Defaults to `0`.
 * @param {string} [options.separator=','] - The character separating columns within a row. Defaults to `,`.
 * @param {boolean} [options.hasHeaders=true] - Whether the CSV includes a header row. If false, generic headers are generated. Defaults to `true`.
 * @return {ProcessCsvData} An object containing the extracted `headers` array and the `numberOfRows` count. Returns default values ({ type: 'csv', headers: [], numberOfRows: 0 }) if csvText is empty, null, undefined, or contains no valid rows after filtering.
 *
 * @example Importing the processCSV utility
 * ```typescript
 * import { processCSV } from '@automator/shared/utils/get-file-data/process-csv';
 * ```
 *
 * @example Process CSV coming from the server
 * If you want to process a CSV coming from the server, use the `getFileData` utility instead of `processCSV`.
 * ```typescript
 * const csvData = await getFileData( 'csv', 'https://example.com/data.csv' );
 * ```
 *
 * @example Default behavior with standard CSV
 * Processes a typical CSV with headers in the first row and comma separators.
 * ```typescript
 * const csvWithHeaders = 'Name,Age,City\nAlice,30,New York\nBob,25,London';
 * processCSV( csvWithHeaders );
 * // Returns: { type: 'csv', headers: [ 'Name', 'Age', 'City' ], numberOfRows: 2 }
 * ```
 *
 * @example CSV without headers
 * Processes CSV data where no header row is present. Generic headers are generated.
 * ```typescript
 * const csvWithoutHeaders = 'Data1,Data2\nVal1,Val2\nVal3,Val4';
 * processCSV( csvWithoutHeaders, { hasHeaders: false } );
 * // Returns: { type: 'csv', headers: [ 'Column1', 'Column2' ], numberOfRows: 3 }
 * ```
 *
 * @example Custom header row and separator
 * Processes CSV data where headers are on a different row (index 1) and columns are separated by semicolons.
 * ```typescript
 * const customCsv = 'Meta Info\nName; Score ; Location\nCharlie; 95 ; Paris\nDavid; 88 ; Tokyo';
 * processCSV( customCsv, { headerRow: 1, separator: ';', hasHeaders: true } );
 * // Returns: { type: 'csv', headers: ['Name', 'Score', 'Location'], numberOfRows: 2 } // Note: Headers are trimmed
 * ```
 *
 * @example Handling empty lines and whitespace
 * Demonstrates that empty lines are ignored and headers are trimmed.
 * ```typescript
 * const messyCsv = ' ID , Value \n\n 1, A \n 2, B \n\n';
 * processCSV( messyCsv );
 * // Returns: { type: 'csv', headers: ['ID', 'Value'], numberOfRows: 2 }
 * ```
 *
 * @example Empty or invalid input
 * Returns default empty structure for null, undefined, or empty string input.
 * ```typescript
 * processCSV(null);
 * // Returns: { type: 'csv', headers: [], numberOfRows: 0 }
 * processCSV( '' );
 * // Returns: { type: 'csv', headers: [], numberOfRows: 0 }
 * processCSV( '\n\n' ); // Only empty lines
 * // Returns: { type: 'csv', headers: [], numberOfRows: 0 }
 * ```
 *
 * @remarks
 * - The function filters out rows that are empty after trimming whitespace.
 * - Headers extracted using `hasHeaders: true` are automatically trimmed of leading/trailing whitespace.
 * - If `hasHeaders: true` and the specified `headerRow` is out of bounds, it's clamped to the valid range of row indices.
 */
export function processCSV(csvText, { headerRow = 0, separator = ',', hasHeaders = true, } = {}) {
    if (isEmpty(csvText)) {
        return {
            type: 'csv',
            headers: [],
            numberOfRows: 0,
        };
    }
    // Split into rows and filter out empty lines
    const rows = csvText.split('\n').filter(row => row.trim() !== '');
    // Handle case where there are no valid rows after filtering
    if (rows.length === 0) {
        return {
            type: 'csv',
            headers: [],
            numberOfRows: 0,
        };
    }
    let headers;
    let numberOfRows;
    if (hasHeaders) {
        // Ensure headerRow is within bounds
        const effectiveHeaderRow = Math.max(0, Math.min(headerRow, rows.length - 1));
        // Trim headers to remove potential leading/trailing whitespace
        headers = rows[effectiveHeaderRow].split(separator).map(header => header.trim());
        // Number of data rows is total rows minus the header row
        numberOfRows = rows.length - 1;
    }
    else {
        // Generate generic headers based on the number of columns in the first row
        headers = rows[0].split(separator).map((_, index) => `Column${index + 1}`);
        // All rows are data rows
        numberOfRows = rows.length;
    }
    // Ensure numberOfRows is not negative if input was unusual (e.g., only header row)
    numberOfRows = Math.max(0, numberOfRows);
    return {
        type: 'csv',
        headers: headers,
        numberOfRows: numberOfRows,
    };
}
;
